<template>

<div qid="integrity-check-section"
     id="update_inspection"
>
  <br>
  <div class="card">
    <div class="card-header">
      <div class="float-right">
        <span class="btn btn-success"
              @click="showFormFunction"
              qid="risk-edit-button"
              v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
        >
          <font-awesome-icon icon="pen"/> {{ $t('system.edit') }}
        </span>
      </div>
      <div class="float-left">
        <h5 class="mb-2" qid="integrity-check-table-title">
          {{ $t('maintenance.third_parties.create.inspection_label') }}
        </h5>
        <span class="cursor-pointer task-label-open mr-2"
              v-if="getTask('update_inspection')"
              @click="openToolbar(getTask('update_inspection'))"
        >
          <font-awesome-icon icon="info-circle" class="text-primary"/>
          {{ $t('tasks.open_label') }}
        </span>
        <span v-if="getGuide('update_inspection')">
          <GuideButton :guide="getGuide('update_inspection')"></GuideButton>
        </span>
      </div>
    </div>

    <div class="row no-gutters">
      <div class="col-sm-2 card-menu">
        <div class="card-body text-dark">
          <span class="mt-2">
            {{ $t('maintenance.third_parties.create.inspection_label') }}
          </span>
        </div>
      </div>
      <div class="col-sm-10">
        <div class="card-body">
          <div class="mw-500" v-if="showForm">
            <multiselect
                v-model="form.inspection"
                :options="inspectionItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('maintenance.third_parties.create.inspection_placeholder')"
                label="name"
                qid="edit-tp-form-inspection"
            ></multiselect>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <p qid="risk-dpia">
                <span qid="tp-inspection" v-if="thirdParty.inspection">
                  {{ $t('maintenance.third_parties.inspection.' + $options.filters.slugify(thirdParty.inspection)) }}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row no-gutters">
      <div class="col-sm-2 card-menu">
        <div class="card-body text-dark">
          <span class="mt-2">
            {{ $t('maintenance.third_parties.create.inspection_date_label') }}
          </span>
        </div>
      </div>
      <div class="col-sm-10">
        <div class="card-body">
          <div class="mw-500" v-if="showForm">
            <datepicker
                class="date-picker"
                qid="edit-tp-form-inspection-date"
                v-model="form.inspection_updated_at"
                :clear-button="true"
                :placeholder="$t('maintenance.third_parties.create.inspection_date_placeholder')"
            ></datepicker>
          </div>
          <div v-if="!showForm">
            <div v-if="thirdParty">
              <div qid="risk-dpia">
                {{ thirdParty.inspection_updated_at | formatDate }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row no-gutters">
      <div class="col-sm-2 card-menu">
        <div class="card-body text-dark">
          <span>
            {{ $t('maintenance.third_parties.create.dpa_description_label') }}
          </span>
        </div>
      </div>
      <div class="col-sm-10">
        <div class="card-body">
          <div class="mw-500" v-if="showForm">
            <b-form-textarea
              qid="risk-action-description"
              v-model="form.inspection_description"
              rows="4"
              max-rows="4"
              :placeholder="$t('maintenance.third_parties.create.dpa_description_placeholder')"
            ></b-form-textarea>
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="cancelForm"
                        qid="risk-form-cancel-button"
              >
                {{ $t('processes.legal_basis.create.cancel') }}
              </b-button>
              <b-button class="mt-3 ml-3"
                        variant="success"
                        @click="formSubmit"
                        qid="risk-form-submit-button"
              >
                {{ $t('processes.legal_basis.create.submit') }}
              </b-button>
            </div>
          </div>
          <div v-if="!showForm">
            <div class="text-formatted"
                 qid="risk-action-plan"
                 style="min-height: 105px;"
            >
              {{ thirdParty.inspection_description }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row no-gutters">
      <div class="col-sm-2 card-menu">
        <div class="card-body text-dark">
          <span>
            {{ $t('attachments.label') }}
            <br>
            <span>({{ $t('attachments.file_size_label') }})</span>
          </span>
        </div>
      </div>
      <div class="col-sm-10">
        <div class="card-body">
          <div v-if="!showForm" class="mw-500">
            <b-alert v-if="error.length"
                     show
                     variant="danger"
                     qid="attachments-p-error"
            >
              {{ error }}
            </b-alert>
            <p v-for="file in thirdParty.media">
              <span v-if="file.collection_name == 'inspection'">
                <span class="float-right text-muted cursor-pointer"
                      @click="openDeleteAttachmentModal(file)"
                      qid="remove-media-item"
                      v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
                >
                  <font-awesome-icon icon="trash-alt" />
                </span>
                <span class="mr-3 float-right text-muted cursor-pointer"
                      @click="downloadMedia(file)"
                      qid="download-media"
                >
                  <font-awesome-icon icon="cloud-download-alt" />
                </span>
                <a @click="downloadMedia(file)" qid="create-p-media-item">
                  {{ file.file_name }}
                </a>
              </span>
            </p>
            <label v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
                   class="btn btn-secondary cursor-pointer"
                   qid="create-p-file-button"
            >
              <input type="file"
                     name="fileupload"
                     value="fileupload"
                     multiple
                     id="file"
                     @change="onFileChange"
                     @click="resetFile"
              >
              <span>
                <font-awesome-icon icon="cloud-upload-alt" />
                {{ $t('system.upload') }}
              </span>
            </label>
          </div>
        </div>
      </div>
    </div>

    <div class="row no-gutters">
      <div class="col-sm-2 card-menu">
        <div class="card-body text-dark">
          <span>{{ $t('links.label') }}</span>
        </div>
      </div>
      <div class="col-sm-10">
        <div class="card-body">
          <div class="mw-500" v-if="!showForm">
            <div v-if="thirdParty.links">
              <p v-for="link in thirdParty.links" >
                <span v-if="link.collection == 'inspection'">
                  <span class="float-right text-muted cursor-pointer"
                        @click="openDeleteLinkModal(link)"
                        qid="view-p-remove-link"
                        v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
                  >
                    <font-awesome-icon icon="trash-alt" />
                  </span>
                  <a :href="link.url" target="_blank" qid="create-p-link-item">
                    {{ link.name ? link.name : link.url }}
                  </a>
                </span>
              </p>
            </div>
            <div v-if="showInput">
              <label class="mt-2">{{ $t('system.link') }}</label>
              <b-form-input
                type="text"
                aria-describedby="inputGroup"
                v-model="inputLink"
                :placeholder="$t('maintenance.third_parties.create.links_placeholder')"
                qid="create-p-links"
              ></b-form-input>
              <label class="mt-2">{{ $t('table.name') }}</label>
              <b-form-input
                type="text"
                aria-describedby="inputGroup"
                v-model="inputLinkName"
                :placeholder="inputLink ? inputLink : $t('table.name')"
                qid="create-p-links"
              ></b-form-input>
              <div class="text-right mt-2">
                <span class="btn btn-success cursor-pointer mr-0"
                      id="inputGroup"
                      @click="handleLink"
                      qid="handle-link-save"
                >
                  {{ $t('system.save') }}
                </span>
              </div>
            </div>
            <span class="btn btn-link cursor-pointer"
                  @click="showInput = true"
                  qid="add-another-link"
                  v-if="isGranted([permissions.THIRD_PARTY_EDIT, permissions.THIRD_PARTY_EDIT_OWN], thirdParty) && (!getClient.parent || (getClient.parent && thirdParty.partition))"
            >
              <font-awesome-icon icon="plus"/> {{ $t('system.add_link') }}
            </span>
          </div>
        </div>
      </div>
    </div>

  </div>

  <ConfirmRemoveAttachment
      :object="selectedFile"
      :show="showDeleteAttachmentModal"
      type="file"
      @closeConfirmModal="closeDeleteAttachmentModal"
  ></ConfirmRemoveAttachment>

  <ConfirmRemoveAttachment
      :object="selectedLink"
      :show="showDeleteLinkModal"
      type="link"
      @closeConfirmModal="closeDeleteLinkModal"
  ></ConfirmRemoveAttachment>

</div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import ConfirmRemoveAttachment from '@/components/ConfirmRemoveAttachment.vue';
  import Datepicker from 'vuejs-datepicker';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'TransfersDisclosures',
    data: function() {
      return {
        form: {
          inspection: [],
          inspection_updated_at: '',
          inspection_description: '',
        },
        description: '',
        error: '',
        loadingItems: false,
        showForm: false,
        inspectionItems: [
          { name: this.$t('maintenance.third_parties.inspection.no'), value: 'No' },
          { name: this.$t('maintenance.third_parties.inspection.external_audit'), value: 'External audit' },
          { name: this.$t('maintenance.third_parties.inspection.site_visit'), value: 'Site visit' },
          { name: this.$t('maintenance.third_parties.inspection.third-party_self-audit'), value: 'Third-party self-audit' }
        ],
        selectedCountries: [],
        countries: [],
        thirdParty: {},
        showInput: false,
        inputLink: '',
        inputLinkName: '',
        selectedFile: {},
        selectedLink: {},
        showDeleteAttachmentModal: false,
        showDeleteLinkModal: false,
        myGuides: []
      }
    },
    props: {
      object: {
        type: Object
      },
      tasks: {
        type: Array
      }
    },
    computed: {
      getClient() {
        return this.$store.state.client
      }
    },
    components: {
      ValidationForm,
      ValidationList,
      Datepicker,
      ConfirmRemoveAttachment,
      GuideButton
    },
    watch: {
      object: function (value) {
        this.thirdParty = value
      }
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.tasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Third party' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Third party',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      openDeleteAttachmentModal(object) {
        this.selectedFile = object
        this.showDeleteAttachmentModal = true
      },
      closeDeleteAttachmentModal () {
        this.selectedFile = {}
        this.showDeleteAttachmentModal = false;
        this.$emit('updated');
      },
      openDeleteLinkModal(object) {
        this.selectedLink = object
        this.showDeleteLinkModal = true
      },
      closeDeleteLinkModal () {
        this.selectedLink = {}
        this.showDeleteLinkModal = false;
        this.$emit('updated');
      },
      resetFile() {
        let imagefile = document.querySelector('#file');
        imagefile.value = '';
      },
      handleLink() {
        let self = this;
        let formData = new FormData();

        if (!this.inputLink.startsWith('http')) {
          this.inputLink = 'http://' + this.inputLink
        }

        formData.append("url", this.inputLink);
        formData.append("name", this.inputLinkName);
        formData.append("model_type", this.thirdParty.who_am_i);
        formData.append("model_id", this.thirdParty.id);
        formData.append("collection", 'inspection');

        piincHttp.post('links', formData).then(function() {
          self.$toastr('success', self.$t('maintenance.third_parties.link_add_success'))
          self.$emit('updated');
          self.inputLink = ''
          self.inputLinkName = ''
          self.showInput = false
        }, function() {})
      },
      downloadMedia(object){
        piincHttp.get('documents/' + object.id + '/download', {responseType: 'blob'}).then(function(response) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.download = object.file_name;

          link.click();
          link.remove()
          window.URL.revokeObjectURL(url);
        }, function() {});
      },
      onFileChange(file) {
        let files = file.target.files;
        let self = this;
        let formData = new FormData();
        let sizeError = false;

        for (var i = 0; i < files.length; i++) {
          // Equal to 10 MB
          if (files[i].size > 10485760) {
            sizeError = true;
          }
          formData.append('document[]', files[i]);
        }

        formData.append("name", "documents");
        formData.append("model_type", this.thirdParty.who_am_i);
        formData.append("model_id", this.thirdParty.id);
        formData.append("collection", 'inspection');
        if (sizeError) {
          this.error = this.$t('system.file_size')
        } else {
          piincHttp.post('documents', formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }).then(function() {
            self.$toastr('success', self.$t('maintenance.third_parties.media_add_success'))
            self.error = ''
            self.$emit('updated');
          }, function(error) {
            self.error = error.data.message;
            if (error.data.errors['document.0']) {
              self.error += ' ' + error.data.errors['document.0'][0]
            }
          })
        }
      },
      formSubmit(evt) {
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        var moment = require('moment');
        evt.preventDefault();

        this.form.inspection = this.form.inspection ? this.form.inspection.value : '';
        this.form.inspection_updated_at = this.form.inspection_updated_at ? moment(this.form.inspection_updated_at).format('YYYY-MM-DD') : ''

        piincHttp.put('third-parties/' + this.thirdParty.slug, this.form).then(function(response) {
          self.thirdParty = response.data
          self.$emit('updated');
          self.showForm = false;
          self.$toastr('success', self.$t('maintenance.third_parties.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      cancelForm() {
        this.showForm = false
      },
      showFormFunction() {
        this.setDefaultForm(this.thirdParty)
        this.showForm = true
      },
      setDefaultForm(object) {
        let self = this;

        this.form.inspection = []
        if (object.inspection) {
          this.form.inspection = _.find(this.inspectionItems, function(item) {
            return item.value === object.inspection;
          });
        }

        this.form.inspection_updated_at = object.inspection_updated_at;
        this.form.inspection_description = object.inspection_description;
      },
    },
    created () {
      this.thirdParty = this.object
      if (this.getClient && this.getClient.slug) {

      }
      this.loadGuides('update_inspection');
    }
  }
</script>
